import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, Loader, CheckCircle, ArrowLeft, AlertCircle } from "lucide-react";
import { useUserStore } from "../stores/useUserStore";

const ResetPasswordPage = () => {
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [errors, setErrors] = useState({});

	const { token } = useParams();
	const navigate = useNavigate();
	const { resetPassword, loading } = useUserStore();

	// בדיקת תקינות הסיסמה
	useEffect(() => {
		const newErrors = {};

		if (password) {
			if (password.length < 6) {
				newErrors.password = "הסיסמה חייבת להכיל לפחות 6 תווים";
			}
		}

		if (confirmPassword && password !== confirmPassword) {
			newErrors.confirmPassword = "הסיסמאות אינן תואמות";
		}

		setErrors(newErrors);
	}, [password, confirmPassword]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		if (Object.keys(errors).length > 0 || !password || !confirmPassword) {
			return;
		}

		const result = await resetPassword(token, password);
		
		if (result.success) {
			setIsSuccess(true);
			// מעבר לדף הלוגין אחרי 3 שניות
			setTimeout(() => {
				navigate('/login');
			}, 3000);
		}
	};

	// אם האיפוס הצליח
	if (isSuccess) {
		return (
			<div className='flex flex-col justify-center py-14 mt-10 sm:px-6 lg:px-8 bg-white'>
				<motion.div
					className='sm:mx-auto sm:w-full sm:max-w-md'
					initial={{ opacity: 0, scale: 0.9 }}
					animate={{ opacity: 1, scale: 1 }}
					transition={{ duration: 0.8 }}
				>
					<div className='bg-white border border-gray-200 py-8 px-4 shadow sm:rounded-lg sm:px-10'>
						<div className='text-center'>
							<motion.div
								initial={{ scale: 0 }}
								animate={{ scale: 1 }}
								transition={{ duration: 0.5, delay: 0.2 }}
								className='mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-6' style={{backgroundColor: 'rgba(100, 13, 70, 0.1)'}}
							>
								<CheckCircle className='h-8 w-8 text-brand-primary' />
							</motion.div>
							
							<h2 className='text-2xl font-extrabold text-brand-primary mb-4'>
								הסיסמה אופסה! 🎉
							</h2>
							
							<div className='rounded-lg p-4 mb-6' style={{backgroundColor: 'rgba(100, 13, 70, 0.05)', borderColor: 'rgba(100, 13, 70, 0.2)', color: 'rgba(100, 13, 70, 0.8)'}}>
								<p className='text-emerald-800 text-sm leading-relaxed'>
									הסיסמה החדשה שלך פעילה כעת.
									<br />
									תוכל כעת להתחבר עם הסיסמה החדשה.
								</p>
							</div>
							
							<div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
								<p className='text-blue-800 text-xs leading-relaxed'>
									מעבר אוטומטי לדף ההתחברות תוך 3 שניות...
								</p>
							</div>
							
							<Link
								to='/login'
								className='w-full flex justify-center py-2 px-4 border border-transparent 
								rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary
								hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2
								focus:ring-brand-primary transition duration-150 ease-in-out'
							>
								התחבר עכשיו
							</Link>
						</div>
					</div>
				</motion.div>
			</div>
		);
	}

	return (
		<div className='flex flex-col justify-center py-12 sm:px-6 lg:px-8 bg-white'>
			<motion.div
				className='sm:mx-auto sm:w-full sm:max-w-md'
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
			>
				<h2 className='mt-6 text-center text-3xl font-extrabold text-brand-primary'>
					סיסמה חדשה 🔐
				</h2>
				<p className='mt-2 text-center text-sm text-gray-600'>
					הכנס סיסמה חדשה ובטוחה
				</p>
			</motion.div>

			<motion.div
				className='mt-8 sm:mx-auto sm:w-full sm:max-w-md'
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, delay: 0.2 }}
			>
				<div className='bg-white border border-gray-200 py-8 px-4 shadow sm:rounded-lg sm:px-10'>
					<form onSubmit={handleSubmit} className='space-y-6'>
						{/* סיסמה חדשה */}
						<div>
							<label htmlFor='password' className='block text-sm font-medium text-gray-700'>
								סיסמה חדשה
							</label>
							<div className='mt-1 relative rounded-md shadow-sm'>
								<div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
									<Lock className='h-5 w-5 text-gray-600' aria-hidden='true' />
								</div>
								<input
									id='password'
									type={showPassword ? 'text' : 'password'}
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className={`block w-full px-3 py-2 pl-10 pr-10 bg-gray-50 border 
									${errors.password ? 'border-red-500' : 'border-gray-300'} 
									rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-primary 
									focus:border-brand-primary sm:text-sm`}
									placeholder='הכנס סיסמה חדשה'
									disabled={loading}
								/>
								<button
									type='button'
									className='absolute inset-y-0 right-0 pr-3 flex items-center'
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? (
										<EyeOff className='h-5 w-5 text-gray-600 hover:text-gray-700' />
									) : (
										<Eye className='h-5 w-5 text-gray-600 hover:text-gray-700' />
									)}
								</button>
							</div>
							{errors.password && (
								<p className='mt-1 text-sm text-red-500'>{errors.password}</p>
							)}
						</div>

						{/* אישור סיסמה */}
						<div>
							<label htmlFor='confirmPassword' className='block text-sm font-medium text-gray-700'>
								אישור סיסמה
							</label>
							<div className='mt-1 relative rounded-md shadow-sm'>
								<div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
									<Lock className='h-5 w-5 text-gray-600' aria-hidden='true' />
								</div>
								<input
									id='confirmPassword'
									type={showConfirmPassword ? 'text' : 'password'}
									required
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									className={`block w-full px-3 py-2 pl-10 pr-10 bg-gray-50 border 
									${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} 
									rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-primary 
									focus:border-brand-primary sm:text-sm`}
									placeholder='הכנס שוב את הסיסמה החדשה'
									disabled={loading}
								/>
								<button
									type='button'
									className='absolute inset-y-0 right-0 pr-3 flex items-center'
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								>
									{showConfirmPassword ? (
										<EyeOff className='h-5 w-5 text-gray-600 hover:text-gray-700' />
									) : (
										<Eye className='h-5 w-5 text-gray-600 hover:text-gray-700' />
									)}
								</button>
							</div>
							{errors.confirmPassword && (
								<p className='mt-1 text-sm text-red-500'>{errors.confirmPassword}</p>
							)}
						</div>

						{/* הנחיות סיסמה */}
						<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
							<div className='flex items-start'>
								<AlertCircle className='h-5 w-5 text-blue-600 mt-0.5 ml-2' />
								<div className='text-blue-800 text-sm text-right'>
									<p className='font-medium mb-1'>דרישות סיסמה:</p>
									<ul className='list-disc list-inside space-y-1 text-xs'>
										<li className={password.length >= 6 ? 'text-brand-primary' : ''}>
											לפחות 6 תווים
										</li>
										<li className={password && confirmPassword && password === confirmPassword ? 'text-brand-primary' : ''}>
											הסיסמאות חייבות להיות זהות
										</li>
									</ul>
								</div>
							</div>
						</div>

						<button
							type='submit'
							className='w-full flex justify-center py-2 px-4 border border-transparent 
							rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary
							hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2
							focus:ring-brand-primary transition duration-150 ease-in-out disabled:opacity-50
							disabled:cursor-not-allowed'
							disabled={loading || Object.keys(errors).length > 0 || !password || !confirmPassword}
						>
							{loading ? (
								<>
									<Loader className='mr-2 h-5 w-5 animate-spin' aria-hidden='true' />
									מעדכן סיסמה...
								</>
							) : (
								<>
									<CheckCircle className='mr-2 h-5 w-5' aria-hidden='true' />
									עדכן סיסמה
								</>
							)}
						</button>
					</form>

					<div className='mt-6'>
						<Link
							to='/login'
							className='w-full flex justify-center items-center py-2 px-4 border border-gray-300 
							rounded-md shadow-sm text-sm font-medium text-gray-700 bg-gray-50
							hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2
							focus:ring-brand-primary transition duration-150 ease-in-out'
						>
							<ArrowLeft className='mr-2 h-4 w-4' />
							חזור להתחברות
						</Link>
					</div>
				</div>
			</motion.div>
		</div>
	);
};

export default ResetPasswordPage; 